import React, { useRef } from 'react';
import styles from './PinLengthToggle.module.css';

const DEFAULT_OPTIONS = [4, 6];

/**
 * Selector segmentado "4 dígitos / 6 dígitos" para elegir la longitud del PIN.
 * Accesible como radiogroup: Tab entra al grupo, flechas/Inicio/Fin cambian
 * la opción, Espacio/Enter la confirman.
 */
export default function PinLengthToggle({
  value,
  onChange,
  disabled = false,
  options = DEFAULT_OPTIONS,
  /** Nombre accesible del grupo. */
  label = 'Longitud del PIN',
  /** Texto corto opcional debajo del selector (ej. "6 dígitos es más seguro"). */
  hint,
  className = '',
}) {
  const refs = useRef([]);
  const activeIndex = Math.max(0, options.indexOf(value));

  const select = (index) => {
    const next = options[index];
    refs.current[index]?.focus();
    if (next !== value) onChange?.(next);
  };

  const handleKeyDown = (e, index) => {
    if (disabled) return;
    let target = null;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = (index + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = (index - 1 + options.length) % options.length;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = options.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    select(target);
  };

  return (
    <div className={`${styles.wrap} ${className}`}>
      <div
        className={styles.group}
        role="radiogroup"
        aria-label={label}
        aria-disabled={disabled || undefined}
      >
        {options.map((opt, i) => {
          const checked = i === activeIndex;
          return (
            <button
              key={opt}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              className={`${styles.option} ${checked ? styles.active : ''}`}
              disabled={disabled}
              onClick={() => select(i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            >
              {opt} dígitos
            </button>
          );
        })}
      </div>
      {hint && <p className={styles.hint}>{hint}</p>}
    </div>
  );
}
